import React, { useMemo } from 'react';
import CustomModal from '../../baseComponents/gui/customModal/CustomModal';
import { safeHTML } from '../../../utils/safeHTML';
import CustomButton from '../../features/customButton/ui/main/CustomButton';
import { useModal } from '../../../hooks/useModal';
import Icon from '../../baseComponents/gui/icon/Icon';
import classNames from 'classnames';
import { useUser } from '../../../redux/reducer/user';
import { shopModal } from '../../../constants/copyright-modals';
import { useSendProductToGoldenAppleStore } from '../../../hooks/useSendProductToGoldenAppleStore';
import AppLoader from '../../features/appLoader/AppLoader';
import { useContent, useProducts } from '../../../redux/reducer/content';
import { useBuyOrPutItem } from '../../../hooks/useBuyOrPutItem';
import { image } from '../../../utils/data/baseUrl';
import { useTranslation } from '../../../translations';
import { useLanguageControls } from '../../../hooks/useLanguageControls';

const {
  buttons: { buy, elfBuy, favorites },
} = shopModal;

const ShopModal = React.forwardRef(function ShopModal({ itemId, productId, callbacks }, ref) {
  const {
    profile: { items, money, gacha_event_cash },
  } = useUser();
  const { t } = useTranslation();
  const { notFilteredShopItems } = useContent();
  const { buyProducts } = useProducts();
  const { getLocalizedCurrency } = useLanguageControls();
  const { title, beauty_routine_id, sku, link, description, new_price, hasFavourite } = useMemo(
    () => buyProducts.find(({ id }) => id === productId),
    [buyProducts, productId],
  );

  const { preview_image, cost, giftCost, reward_score } = useMemo(
    () => notFilteredShopItems.find(({ id }) => id === itemId),
    [notFilteredShopItems, itemId],
  );

  const truthCost = giftCost || cost;

  const isCanBuy = money >= (cost || 0) && gacha_event_cash >= (giftCost || 0);

  const necessaryItem = items?.find?.(({ id }) => id === itemId);

  const status = !necessaryItem ? 'absent' : necessaryItem?.is_equipped ? 'equipped' : 'unequipped';

  const { closeModal, id } = useModal();

  const close = () => {
    closeModal(id);
  };

  const elfBuyCallback = () => {
    ({
      absent() {
        callbacks.buyItem(itemId);
      },
      equipped() {},
      unequipped() {
        callbacks.equipItem(itemId);
      },
    })[status]?.();
  };

  const {
    favoriteCallback,
    basketCallback,
    hasBasket,
    isPending: isPendingStoreRequests,
  } = useSendProductToGoldenAppleStore({ callbacks, beauty_routine_id, id: productId, sku, link, hasFavourite });

  const isPendingItem = useBuyOrPutItem({ callbacks });
  return (
    <CustomModal>
      <div className={'shop-modal'} ref={ref}>
        <div className={'shop-modal__content'}>
          <CustomButton className={'shop-modal__close custom-button_cross'} icon={'buttons/cross'} onClick={close} />
          <div className={'shop-modal__panel'}>
            {[money, gacha_event_cash].map((value, i) => (
              <div key={`p${i}`} className={'shop-modal__panel-item'}>
                <div className={'shop-modal__panel-item-icon'}>
                  <Icon name={`shop/${{ 1: 'coin', 2: 'gift' }[++i]}-icon`} />
                </div>
                <div className={'shop-modal__panel-item-value'}>{value}</div>
              </div>
            ))}
          </div>
          <div className={'shop-modal__preview'}>
            <img src={preview_image} alt={'item-img'} />
          </div>
          <h2 className={'shop-modal__name'}>{safeHTML(title)}</h2>
          <p className={'shop-modal__description'}>{safeHTML(description)}</p>
          {!!reward_score && !necessaryItem && (
            <div className={'shop-modal__hp'}>
              <div className={'shop-modal__hp-title'}>{safeHTML(`+${reward_score}XP`)}</div>
              <div className={'shop-modal__hp-text'}>{safeHTML(t('other.toRating'))}</div>
            </div>
          )}

          <div className={'shop-modal__buttons'}>
            <CustomButton
              {...buy.attr}
              className={classNames(buy.attr.className, { 'shop-modal__favorite_has-basket': hasBasket })}
              text={
                !hasBasket ? `${t(buy.text.have, { cost: new_price })}${getLocalizedCurrency()}` : buy.text.dontHave
              }
              disabled={hasBasket}
              onClick={basketCallback}
            />
            <CustomButton
              {...favorites.attr}
              className={classNames(favorites.attr.className, {
                'shop-modal__favorite_has-favorite': hasFavourite,
              })}
              disabled={hasFavourite}
              onClick={favoriteCallback}
            />
            <CustomButton
              {...elfBuy.attr}
              className={classNames(elfBuy.attr.className, {
                'shop-modal__elf-buy_equipped': { equipped: true, absent: !isCanBuy }[status],
              })}
              text={elfBuy.text[status]}
              icon={elfBuy.icon[status]}
              {...(!!truthCost && { count: { absent: truthCost }[status] })}
              {...(!!cost && { coins: { absent: !!cost }[status] })}
              {...(!!giftCost && { image: { absent: image('reward/gift.png') }[status] })}
              disabled={{ equipped: true, absent: !isCanBuy }[status]}
              onClick={elfBuyCallback}
            />
          </div>
        </div>
        {(isPendingItem || isPendingStoreRequests) && <AppLoader />}
      </div>
    </CustomModal>
  );
});

export default ShopModal;
ShopModal.propTypes = {};
